import { z } from 'zod'
import { guidSchema, isNullish } from '../..'
import { bzOptional, EmailAddressValue, ForCompany } from '..'
import { emailAddressValueSchema } from '../Email/EmailTypes'

export const EMBEDDED_REPORTING_PULSE_SCHEDULE_TYPES = ['daily', 'weekly', 'monthly'] as const
export type EmbeddedReportingPulseScheduleType = (typeof EMBEDDED_REPORTING_PULSE_SCHEDULE_TYPES)[number]

export const EMBEDDED_REPORTING_PULSE_DAYS_OF_WEEK = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
export type EmbeddedReportingPulseDayOfWeek = (typeof EMBEDDED_REPORTING_PULSE_DAYS_OF_WEEK)[number]

export const EMBEDDED_REPORTING_PULSE_MONTHLY_FRAMES = ['first', 'mid', 'last'] as const // 'mid' corresponds to 15th
export type EmbeddedReportingPulseMonthlyFrame = (typeof EMBEDDED_REPORTING_PULSE_MONTHLY_FRAMES)[number]

export const EmbeddedReportingPulseScheduleTypeDisplayNames = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
} as const

export const EmbeddedReportingPulseDayOfWeekDisplayNames = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
} as const

export const EmbeddedReportingPulseMonthlyFrameDisplayNames = {
  first: 'First',
  mid: '15th (Midpoint)',
  last: 'Last',
} as const

export const ScheduleDetailsSchema = z.object({
  scheduleHour: z.number().min(0).max(23), // 0-23 hour format
  scheduleDay: bzOptional(z.enum(EMBEDDED_REPORTING_PULSE_DAYS_OF_WEEK)),
  scheduleFrame: bzOptional(z.enum(EMBEDDED_REPORTING_PULSE_MONTHLY_FRAMES)),
})

export type ScheduleDetails = z.infer<typeof ScheduleDetailsSchema>
export const EmbeddedReportingPulseFormBaseSchema = ScheduleDetailsSchema.extend({
  scheduleType: z.enum(EMBEDDED_REPORTING_PULSE_SCHEDULE_TYPES),
  recipients: z.array(emailAddressValueSchema).min(1, 'At least one recipient is required'),
})

export type EmbeddedReportingPulseRecipientArray = EmailAddressValue[]

export const EmbeddedReportingPulseFormSchema = EmbeddedReportingPulseFormBaseSchema.superRefine((data, ctx) => {
  // Conditional validation based on scheduleType
  if ((data.scheduleType === 'weekly' || data.scheduleType === 'monthly') && !data.scheduleDay) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Day of week is required for weekly/monthly schedule',
      path: ['scheduleDay'],
    })
  }
  if (data.scheduleType === 'monthly' && !data.scheduleFrame) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Frame (First/Last/Midpoint) is required for monthly schedule',
      path: ['scheduleFrame'],
    })
  }
  // Ensure day/frame are null if not applicable
  if (data.scheduleType === 'daily') {
    if (!isNullish(data.scheduleDay)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Day should not be set for daily schedule',
        path: ['scheduleDay'],
      })
    }
    if (!isNullish(data.scheduleFrame)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Frame should not be set for daily schedule',
        path: ['scheduleFrame'],
      })
    }
  }
  if (data.scheduleType === 'weekly') {
    if (!isNullish(data.scheduleFrame)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Frame should not be set for weekly schedule',
        path: ['scheduleFrame'],
      })
    }
  }
})

export type EmbeddedReportingPulseFormData = z.infer<typeof EmbeddedReportingPulseFormSchema>

export const EmbeddedReportingPulseCreateInputSchema = EmbeddedReportingPulseFormBaseSchema.extend({
  externalReportId: z.string(),
})

export type EmbeddedReportingPulseCreateInput = ForCompany<z.infer<typeof EmbeddedReportingPulseCreateInputSchema>>

export const EmbeddedReportingPulseUpdateInputSchema = EmbeddedReportingPulseFormBaseSchema.extend({
  pulseGuid: guidSchema,
})

export type EmbeddedReportingPulseUpdateInput = ForCompany<z.infer<typeof EmbeddedReportingPulseUpdateInputSchema>>

export const EmbeddedReportingPulseArchiveInputSchema = z.object({
  pulseGuid: guidSchema,
})

export type EmbeddedReportingPulseArchiveInput = ForCompany<z.infer<typeof EmbeddedReportingPulseArchiveInputSchema>>

// For testing a saved pulse
export const EmbeddedReportingPulseSavedTestInputSchema = z.object({
  type: z.literal('saved'),
  pulseGuid: guidSchema,
})

// For testing an unsaved pulse from form data
export const EmbeddedReportingPulseUnsavedTestInputSchema = z.object({
  type: z.literal('unsaved'),
  dashboardId: z.string(),
  formData: EmbeddedReportingPulseFormSchema,
})

// Union type for the two test scenarios
export const EmbeddedReportingPulseTestInputSchema = z.discriminatedUnion('type', [
  EmbeddedReportingPulseSavedTestInputSchema,
  EmbeddedReportingPulseUnsavedTestInputSchema,
])

export type EmbeddedReportingPulseTestInput = ForCompany<z.infer<typeof EmbeddedReportingPulseTestInputSchema>>
